import React, { useState, useRef, useEffect } from 'react';

interface HelpTooltipProps {
  title: string;
  content: string;
  details?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  mode?: 'hover' | 'click';
}

export const HelpTooltip: React.FC<HelpTooltipProps> = ({ 
  title, 
  content, 
  details,
  position = 'top',
  mode = 'hover' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only add click outside listener for click mode
    if (mode === 'click') {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          tooltipRef.current && 
          !tooltipRef.current.contains(event.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, mode]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom':
        return 'top-full mt-2 left-1/2 -translate-x-1/2';
      case 'left':
        return 'right-full mr-2 top-1/2 -translate-y-1/2';
      case 'right':
        return 'left-full ml-2 top-1/2 -translate-y-1/2';
      case 'top':
      default:
        return 'bottom-full mb-2 left-1/2 -translate-x-1/2';
    }
  };

  const handleMouseEnter = () => {
    if (mode === 'hover') {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      // Show tooltip immediately on hover
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (mode === 'hover') {
      // Add a small delay before hiding to prevent flicker
      hoverTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 100);
    }
  };

  const handleClick = () => {
    if (mode === 'click') {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div 
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        className="ml-1 p-0.5 rounded-full hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
        aria-label="More information"
      >
        <svg 
          className={`w-4 h-4 transition-colors duration-200 ${isOpen ? 'text-indigo-700' : 'text-indigo-500 hover:text-indigo-700'}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
          />
        </svg>
      </button>

      {isOpen && (
        <div 
          ref={tooltipRef}
          className={`absolute z-50 w-72 p-4 bg-white rounded-lg shadow-xl border border-gray-200 ${getPositionClasses()}`}
          style={{ maxWidth: '90vw' }}
        >
          {/* Arrow pointer */}
          <div className={`absolute w-3 h-3 bg-white border-gray-200 transform rotate-45 ${
            position === 'top' ? 'bottom-[-6px] left-1/2 -translate-x-1/2 border-b border-r' :
            position === 'bottom' ? 'top-[-6px] left-1/2 -translate-x-1/2 border-t border-l' :
            position === 'left' ? 'right-[-6px] top-1/2 -translate-y-1/2 border-t border-r' :
            'left-[-6px] top-1/2 -translate-y-1/2 border-b border-l'
          }`} />

          {/* Content */}
          <div className="relative">
            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-start justify-between">
              {title}
              {mode === 'click' && (
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="ml-2 p-0.5 rounded hover:bg-gray-100 transition-colors duration-200"
                  aria-label="Close"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </h4>
            
            <p className="text-xs text-gray-600 leading-relaxed mb-2">
              {content}
            </p>

            {details && (
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 italic">
                  {details}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};